import React, {useCallback, useEffect, useState, useRef} from 'react';
import styled from 'styled-components';
import {getQuestionBaseWithFaqOnly} from '@bootcamp/shared/src/requests';
import {IntercomAPI} from 'react-intercom';
import {Button} from '../../Branding/Buttons';
import {H4, H3, Body2} from '../../Typography';
import {preventDefault} from '../helpers';
import {useModalContext} from '../../../contexts/Modal';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {useAsync} from '@bootcamp/shared/src/util/hooks';
import {Send} from '@styled-icons/fluentui-system-filled/Send'
import {invokeStreamingLambda} from '@bootcamp/shared/src/requests';
import {ArrowCircleUp} from '@styled-icons/fluentui-system-filled/ArrowCircleUp';
import theme from '@bootcamp/shared/src/styles/theme';
const Container = styled.div`
  height: 100%;
  width: 100%;
  max-width: 768px;
  background: ${({theme}) => theme.colors.neutralsPalette.lightGrey};
  overflow-y: scroll;
  transition: transform .4s;
  ${({visible}) => visible
    ? `
      transform: none;
    `
    : `
      transform: translateX(100%);
    `
  }

  padding: 16px;

  ${({theme}) => theme.mediaQueries.tablet} {
    max-width: 100%;
    ${({visible}) => visible
      ? `
        transform: none;
      `
      : `
        transform: translateY(100%);
      `
    }
  }
`;

const CommentContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.colors.neutralsPalette.light};
  border-radius: 8px;
  background: ${({theme}) => theme.colors.neutralsPalette.white};
  * {
    font-family: proxima-nova !important;
  }
  z-index: 5;
`;

const Label = styled(H4)`
  font-weight: 600;
  font-family: proxima-nova;
  color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
  border-bottom: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.colors.neutralsPalette.light};
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 18px;
`;
const BootcampAILabel = styled(H3)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.colors.neutralsPalette.light};
  height: 56px;
`;

const Content = styled(Body2)`
  font-family: proxima-nova;
  color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
  margin-bottom: 16px;
`;

const ConversationContent = styled(Body2)`
  color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
  padding: 16px;
  border-radius: 24px;
  position: relative;
  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
  flex: 0;
  p {
    margin: 0;
  }
`

const UserContent = styled(ConversationContent)`
  background-color: ${({theme}) => theme.darkModeEnabled ? '#2C2C2C' : `#F0EFFC`};
  align-self: flex-end;
  margin-right: 52px;
  &::after {
    background-image: url(${({profilePicture}) => profilePicture});
    background-repeat: no-repeat;
    border-radius: 100px;
    content: '';
    width: 36px;
    height: 36px;
    background-size: cover;
    position: absolute;
    right: -52px;
    top: 8px;
  }

`;

const BotContent = styled(ConversationContent)`
  background-color: ${({theme}) => theme.darkModeEnabled ? '#414141' : '#FAFAFA'};
  align-self: flex-start;
  margin-left: 52px;
  &::before {
    background-image: url('https://dekni9rgkrcu0.cloudfront.net/assets/icons/bootcamp-glyph-black.png');
    background-color: white;
    content: '';
    width: 36px;
    height: 36px;
    background-size: contain;
    position: absolute;
    left: -52px;
    top: 8px;
    border-radius: 100px;
  }
  .dots {
    width: 50px;
    height: 20px;
    border-radius: 10px;
    margin: 10px;
    transform: scale(0);
    animation: overall-scale 8s infinite;
  }

  .dot {
    animation-timing-function: ease-in;
    animation-iteration-count: infinite;
    animation-name: dot-scale;
    animation-duration: 1s;
    
    display: inline-block;
    width: 10px;
    height: 10px;
    margin: 5px 0px;
    border-radius: 10px;
    background-color: #444;
    transform: scale(0.65);
    
    &:first-of-type {
      margin-left: 6px
    }
    &:nth-of-type(2) {
      animation-delay: 0.15s;
    }
    &:nth-of-type(3) {
      animation-delay: 0.3s;
    }
  }

  @keyframes dot-scale {
    0%, 70% { transform: scale(0.65); }
    35% { transform: scale(1); }
  }

  @keyframes overall-scale {
    0%, 95% { transform: scale(0); }
    5%, 90% { transform: scale(1); }
  }

`;

const CloseButton = styled(Button).attrs(props => ({type: 'primary', size: 'large'}))`
  margin-bottom: 16px;
`

const BotInput = styled.input`
  width: 100%;
  border-radius: 24px;
  border: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};
  padding: 11px 16px;
  font-size: 18px;
  line-height: 24px;
  color: ${({theme}) => theme.darkModeEnabled ? 'white' : theme.colors.neutralsPalette.darkGrey};
  background: white;
  box-sizing: border-box;
  outline: none;
  position: relative;
  background: ${({theme}) => theme.darkModeEnabled ? '#1E1E1E' : '#FAFAFA'};
  padding-right: 48px;
`;

const SendButton = styled(ArrowCircleUp).attrs(props => ({size: 32}))``;

const SendButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`
const SubmitButton = styled(Button).attrs(props => ({type: 'primary', size: 'large'}))`
  margin-bottom: 16px;
`

const IntercomButton = styled(Button).attrs(props => ({type: 'primary', size: 'large'}))`
  margin: 16px 0px;
`

const Disclaimer = styled.span`
  color: ${({theme}) => theme.colors.neutralsPalette.grey};
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  display: block;
`

const Comment = ({content=''}) => {
  const [question, answer] = content.split(' [SEPARATE] ');

  return (
    <CommentContainer>
      <Label>Question</Label>
      <Content dangerouslySetInnerHTML={{__html: question}} />
      <Label>Answer</Label>
      <Content dangerouslySetInnerHTML={{__html: answer}} />
    </CommentContainer>
  )
}

const HeaderImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 100px;
  margin-right: 12px;
  background: white;
`;

export const BootcampBot = ({questionId, blockId, isVideo=false, extraDetails=null, placeholder}) => {
  const [query, setQuery] = useState();
  const [model, setModel] = useState('gpt-4o');
  const [conversation, setConversation] = useState([]);
  const [conversationId, setConversationId] = useState();
  const [systemPromptOverride, setSystemPromptOverride] = useState();
  const inputRef = useRef();
  const {bootcamp, profPicUrl, cognitoUser, isAdmin} = useUserDataContext();
  const username = cognitoUser?.username;
  const streamLambdaRequest = useCallback(async () => {
    if (!query) return;
    setConversation(prev => [...prev, query, '']);
    setQuery('');
    await IntercomAPI('trackEvent', 'asked-bootcamp-ai', {bootcamp});
    const events = await invokeStreamingLambda('BootBot-Streaming-Conversation', {query, questionId, model, conversationId, returnHTML: true, isVideo, extraDetails, systemPromptOverride, blockId});
    const decoder = new TextDecoder();
    for await (const event of events) {
      if (event.InvokeComplete) {
        if (event.InvokeComplete.ErrorCode) {
          console.log("Error Code:", event.InvokeComplete.ErrorCode);
          console.log("Details:", event.InvokeComplete.ErrorDetails);
        }
        break;
      }
      if (event.PayloadChunk) {
        const chunk = decoder.decode(event.PayloadChunk.Payload);
        if (chunk.startsWith('{"conversationId":')) {
          const {conversationId} = JSON.parse(chunk);
          setConversationId(conversationId);
          continue;
        }
        setConversation(conversation => {
          const last = conversation[conversation.length - 1];
          return [...conversation.slice(0, -1), last + chunk]
        });
      }
    }
  }, [query, model]);
  const {pending, error, execute} = useAsync(streamLambdaRequest, false);

  useEffect(() => {
    const handleEnter = (event) => {
      if (event.key === 'Enter' && !pending && query) {
        execute();
        inputRef.current.value = '';
      }
    };
    window.addEventListener('keydown', handleEnter);
    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [pending, execute]);

  const typingDots = `
    <div class="dots">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>
  `
  return (
    <CommentContainer>
      <BootcampAILabel><HeaderImage src="https://dekni9rgkrcu0.cloudfront.net/assets/icons/bootcamp-glyph-black.png" />Ask Bootcamp AI</BootcampAILabel>
        {conversation.length > 0 && !error && <ConversationContainer>
        {[...conversation].reverse().map((content, index) => index % 2 === 1 ? <UserContent key={index} children={content} profilePicture={profPicUrl === 'error' ? `https://ui-avatars.com/api/?name=${username}&length=1&background=0D8ABC&color=fff&rounded=true` : profPicUrl} /> : <BotContent key={index} dangerouslySetInnerHTML={{__html: content || typingDots}} />)}
        </ConversationContainer>}
      <div style={{padding: '16px', maxWidth: '1000px', margin: '0 auto'}}>
        <div style={{position: 'relative', marginBottom: '16px'}}>
          <BotInput ref={inputRef} placeholder={"Ask Bootcamp AI a question..."} onChange={e => setQuery(e.target.value)} />
          {!pending && (
            <SendButtonContainer onClick={() => execute()}>
              <SendButton color={query ? theme.colors.brandPalette.royal.default : '#85868E'} />
            </SendButtonContainer>
          )}
        </div>
        <Disclaimer onContextMenu={() => {
          if (!isAdmin) return;
          setSystemPromptOverride();
          const systemPromptOverride = window.prompt('Enter system prompt override:');
          if (systemPromptOverride) {
            setSystemPromptOverride(systemPromptOverride);
          }
        }}>Can't find the answer to your question? Chat with Bootcamp AI to get instant help!</Disclaimer>
      </div>
    </CommentContainer>
  )
}

const ConversationContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 16px;
  max-height: 50vh;
  overflow-y: auto;
  padding: 16px;
  max-width: 1000px;
  margin: 0 auto;
`;


const QuestionFAQs = ({questionId, intercomQuestionString, close}) => {
  const [visible, setVisible] = useState()
  const [comments, setComments] = useState([]);
  const {modalDispatch} = useModalContext();
  const {bootcamp} = useUserDataContext();
  useEffect(() => {
    async function fetch () {
      const {data: {getQuestionBase: {comments}}} = await getQuestionBaseWithFaqOnly(questionId) || {data: {getQuestionBase: {comments: {items: []}}}};
      if (comments.items.length || (['dat', 'oat'].includes(bootcamp) && !window.location.pathname.includes('perceptual-ability'))) {
        modalDispatch({type: 'style', modalContainerStyle: theme => `
            background: ${theme.overlays.opacity.dark._300};
            align-items: flex-start;
            justify-content: flex-end;
            z-index: 10;
            ${theme.mediaQueries.tablet} {
              background: ${theme.overlays.opacity.dark._400};
            }
        `});
        setVisible(true);
        setComments(comments.items);
      } else {
        close();
        IntercomAPI('showNewMessage', intercomQuestionString);
      }
    }
    fetch();
  }, []);

  return (
    <Container onClick={preventDefault} visible={visible}>
      <CloseButton onClick={close}>
        Close FAQs
      </CloseButton>
      <BootcampBot questionId={questionId} />
      {comments.map(comment => <Comment {...comment} />)}
    </Container>
  )
}

export default QuestionFAQs;